import { useRef, useState } from "react";
import { Upload, X, Loader2, Link as LinkIcon } from "lucide-react";
import api, { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Reusable image upload field for the admin panel.
 * Stores the resulting public URL (relative or absolute) on the parent form.
 *
 * Props:
 *  - value: string (current URL) or array (when `multiple`)
 *  - onChange(url|urls)
 *  - label, ratio, help
 *  - multiple: if true, value is an array of urls and accepts multiple uploads
 */
export default function ImageUploadField({ value, onChange, label, ratio = "16:9 · max 8MB", help, multiple = false }) {
  const [busy, setBusy] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const inputRef = useRef(null);

  const resolveUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    return `${BACKEND_URL}${u}`;
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.url;
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f));
      if (multiple) onChange([...(Array.isArray(value) ? value : []), ...urls]);
      else onChange(urls[0]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    if (multiple) onChange((value || []).filter((_, i) => i !== idx));
    else onChange("");
  };

  const items = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <p className="text-[11px] text-zinc-500 mt-0.5">Recommended: <span className="text-[#FFAA33]">{ratio}</span>{help ? ` · ${help}` : ""}</p>

      <div className="mt-2 flex flex-wrap gap-3">
        {items.map((u, i) => (
          <div key={`${u}-${i}`} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 group">
            <img src={resolveUrl(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 border border-white/20 text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid="image-remove-btn">
              <X size={12} />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-24 h-24 rounded-xl border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-zinc-400 hover:border-[#FF6B1A]/50 hover:text-white text-[10px] uppercase tracking-widest disabled:opacity-50"
          data-testid="image-upload-btn"
        >
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          {busy ? "Uploading…" : "Upload"}
        </button>

        <input ref={inputRef} type="file" accept="image/*" multiple={multiple} onChange={onPick} className="hidden" />
      </div>

      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={() => setShowUrl((s) => !s)} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white">
          <LinkIcon size={11} /> {showUrl ? "Hide URL field" : "Or paste a URL instead"}
        </button>
      </div>

      {showUrl && !multiple && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or /api/uploads/images/…"
          className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm font-mono"
        />
      )}
      {showUrl && multiple && (
        <textarea
          value={(Array.isArray(value) ? value : []).join("\n")}
          onChange={(e) => onChange(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
          rows={3}
          placeholder="One URL per line"
          className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm font-mono"
        />
      )}
    </div>
  );
}
